import { useState, useEffect, useCallback } from 'react'
import { satsueiDataTorokuService } from '../services/satsueiDataTorokuService'
import { useApiError } from '../../shared/hooks/useApiError'

export const useSatsueiDataToroku = (errorHandlers = null) => {
  // Error handling - use provided handlers or create new ones
  const defaultErrorHandlers = useApiError()
  const { handleApiError, handleApiResponse, handleValidationErrors } = errorHandlers || defaultErrorHandlers
  // Basic state
  const [objectId, setObjectId] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState(null)

  // Object data
  const [objectData, setObjectData] = useState(null)
  const [satsueiMemoCategoryId, setSatsueiMemoCategoryId] = useState('')
  const [kyotenId, setKyotenId] = useState('')
  const [satsueiGuideId, setSatsueiGuideId] = useState('')

  // Registration info
  const [createUserName, setCreateUserName] = useState('')
  const [createDatetime, setCreateDatetime] = useState('')
  const [updateUserName, setUpdateUserName] = useState('')
  const [updateDatetime, setUpdateDatetime] = useState('')

  // Categories and lists
  const [satsueiMemoCategories, setSatsueiMemoCategories] = useState([])
  const [kyotenList, setKyotenList] = useState([])
  const [satsueiGuideList, setSatsueiGuideList] = useState([])
  const [imageFolderList, setImageFolderList] = useState([])
  const [selectedImageFolderId, setSelectedImageFolderId] = useState(99)

  // Form data
  const [satsueiMemoForm, setSatsueiMemoForm] = useState(null)
  const [formScript, setFormScript] = useState('')
  const [formData, setFormData] = useState({})
  const [satsueiMemoColumnDataList, setSatsueiMemoColumnDataList] = useState([])

  // Media data
  const [imageList, setImageList] = useState([])
  const [movieList, setMovieList] = useState([])
  const [otherFileList, setOtherFileList] = useState([])

  // Media type selection
  const [selectedMediaType, setSelectedMediaType] = useState('image') // 'image', 'movie', 'other'

  // Selection mode
  const [isSelectionMode, setIsSelectionMode] = useState(false)
  const [selectedItems, setSelectedItems] = useState([])

  // Auto ID flag
  const [objectIdAutoFlg, setObjectIdAutoFlg] = useState(0)

  // Object existence state
  const [objectExists, setObjectExists] = useState(false)

  // Load initial data
  const loadInitialData = useCallback(async () => {
    setIsLoading(true)
    setError(null)

    try {
      const [categories, kyoten] = await Promise.all([
        satsueiDataTorokuService.getSatsueiMemoCategory({ isContainSatsueiMemoColumn: false }),
        satsueiDataTorokuService.getKyotenList()
      ])

      setSatsueiMemoCategories(categories.satsueiMemoCategoryList || [])
      setKyotenList(kyoten.kyotenList || [])
    } catch (err) {
      handleApiError(err, '初期データの読み込みに失敗しました')
      console.error('Failed to load initial data:', err)
    } finally {
      setIsLoading(false)
    }
  }, [handleApiError])

  // Check and update object existence state
  const updateObjectExistence = useCallback(async (id) => {
    if (!id) {
      setObjectExists(false)
      return
    }

    try {
      const exists = await checkObjectExists(id)
      setObjectExists(exists)
    } catch (err) {
      console.error('Failed to update object existence:', err)
      setObjectExists(false)
    }
  }, [])

  // Load image folder list
  const loadImageFolderList = useCallback(async (id) => {
    try {
      const result = await satsueiDataTorokuService.getImageFolderList(id, true)
      const folders = result.imageFolderList || []
      setImageFolderList(folders)
      
      // If there's only one folder (e.g., "未指定" when no folders registered),
      // automatically set selectedImageFolderId to that folder's ID
      if (folders.length === 1) {
        setSelectedImageFolderId(folders[0].imageFolderId)
      } else if (folders.length === 0) {
        // If no folders at all, reset to default (though this shouldn't happen with isContainMishitei=true)
        setSelectedImageFolderId(99)
      }
    } catch (err) {
      console.error('Failed to load image folder list:', err)
    }
  }, [])

  // Load satsuei guide list
  const loadSatsueiGuideList = useCallback(async (categoryId) => {
    if (!categoryId) return

    try {
      const result = await satsueiDataTorokuService.getSatsueiGuideList(categoryId)
      setSatsueiGuideList(result.satsueiGuideList || [])
    } catch (err) {
      console.error('Failed to load satsuei guide list:', err)
    }
  }, [])

  // Load satsuei memo form
  const loadSatsueiMemoForm = useCallback(async (id, categoryId, changeFlg = false) => {
    try {
      const result = await satsueiDataTorokuService.getSatsueiMemoForm(id, categoryId, changeFlg)
      setSatsueiMemoForm(result)
      setFormScript(result.pcFormScript || '')
    } catch (err) {
      console.error('Failed to load satsuei memo form:', err)
    }
  }, [])

  // Load media lists
  const loadImageList = useCallback(async (id, imageFolderId = null) => {
    try {
      const result = await satsueiDataTorokuService.getImageList(id, imageFolderId)
      // Check for business logic errors in the response
      const hasError = handleApiResponse(result, '画像リストの読み込みに失敗しました')
      if (hasError) {
        return
      }
      setImageList(result.objectDatas?.[0]?.objectImages || [])
    } catch (err) {
      handleApiError(err, '画像リストの読み込みに失敗しました')
      console.error('Failed to load image list:', err)
    }
  }, [handleApiResponse, handleApiError])

  const loadMovieList = useCallback(async (id) => {
    try {
      const result = await satsueiDataTorokuService.getMovieList(id)
      // Check for business logic errors in the response
      const hasError = handleApiResponse(result, '動画リストの読み込みに失敗しました')
      if (hasError) {
        return
      }
      setMovieList(result.movieList || [])
    } catch (err) {
      handleApiError(err, '動画リストの読み込みに失敗しました')
      console.error('Failed to load movie list:', err)
    }
  }, [handleApiResponse, handleApiError])

  const loadOtherFileList = useCallback(async (id) => {
    try {
      const result = await satsueiDataTorokuService.getOtherFileList(id)
      // Check for business logic errors in the response
      const hasError = handleApiResponse(result, 'その他ファイルリストの読み込みに失敗しました')
      if (hasError) {
        return
      }
      setOtherFileList(result.otherFileList || [])
    } catch (err) {
      handleApiError(err, 'その他ファイルリストの読み込みに失敗しました')
      console.error('Failed to load other file list:', err)
    }
  }, [handleApiResponse, handleApiError])

  // Load initial data
  useEffect(() => {
    loadInitialData()
  }, [loadInitialData])

  // Check object existence when objectId changes
  useEffect(() => {
    updateObjectExistence(objectId)
  }, [objectId, updateObjectExistence])

  // Load object data
  const loadObject = useCallback(async (id) => {
    if (!id) {
      handleValidationErrors('IDを入力してください')
      return
    }

    setIsLoading(true)
    setError(null)

    setFormData({})
    setSatsueiMemoColumnDataList([])

    try {
      const object = await satsueiDataTorokuService.getObject(id, satsueiMemoCategoryId, false)

      // Check for business logic errors in the response
      const hasError = handleApiResponse(object, 'オブジェクトの取得に失敗しました')
      if (hasError) {
        return
      }

      setObjectData(object)
      setObjectId(id)
      setSatsueiMemoCategoryId(object.satsueiMemoCategoryId ?? '')
      setKyotenId(object.kyotenId ?? '')
      setSatsueiGuideId(object.satsueiGuideId ?? '')
      setCreateUserName(object.createUserName || '')
      setCreateDatetime(object.createDatetime || '')
      setUpdateUserName(object.updateUserName || '')
      setUpdateDatetime(object.updateDatetime || '')
      setObjectIdAutoFlg(object.objectIdAutoFlg ?? 0)

      // Set the column data list from backend response
      if (object.satsueiMemoColumnDataList) {
        setSatsueiMemoColumnDataList(object.satsueiMemoColumnDataList)

        // Initialize form data from column data
        const initialFormData = {}
        object.satsueiMemoColumnDataList.forEach(column => {
          if (column.deleteFlg === 0) {
            // For choice fields, check the option values
            if (column.satsueiMemoColumnValueList && column.satsueiMemoColumnValueList.length > 0) {
              // First, check if user has made any selections (value === "1")
              let selectedOptions = column.satsueiMemoColumnValueList
                .filter(option => option.deleteFlg === 0 && option.value === "1")
                .map(option => option.key)

              // If no user selections, fall back to initial values (initialValueFlg === 1)
              if (selectedOptions.length === 0) {
                selectedOptions = column.satsueiMemoColumnValueList
                  .filter(option => option.deleteFlg === 0 && option.initialValueFlg === 1)
                  .map(option => option.key)
              }

              if (selectedOptions.length > 0) {
                // For multi-select fields (checkboxes, multi-select), join with commas
                if (column.columnType === 6 || column.columnType === 7) {
                  initialFormData[column.key] = selectedOptions.join(',')
                } else {
                  // For single-select fields (radio buttons, dropdown), use first selected
                  initialFormData[column.key] = selectedOptions[0]
                }
              } else {
                // Store backend format values in formData
                let value = column.value || column.initialValue || ''
                initialFormData[column.key] = value
              }
            } else {
              // Store backend format values in formData
              let value = column.value || column.initialValue || ''
              initialFormData[column.key] = value
            }
          }
        })
        setFormData(initialFormData)
      } else {
        setFormData({})
        setSatsueiMemoColumnDataList([])
      }  
      // Load related data
      await Promise.all([
        loadImageFolderList(id),
        loadSatsueiGuideList(object.satsueiMemoCategoryId),
        loadSatsueiMemoForm(id, object.satsueiMemoCategoryId),
        loadImageList(id),
        loadMovieList(id),
        loadOtherFileList(id)
      ])

    } catch (err) {
      handleApiError(err, 'オブジェクトの読み込みに失敗しました')
      console.error('Failed to load object:', err)
    } finally {
      setIsLoading(false)
    }
  }, [handleApiError, handleApiResponse, handleValidationErrors, satsueiMemoCategoryId, loadImageFolderList, loadSatsueiGuideList, loadSatsueiMemoForm, loadImageList, loadMovieList, loadOtherFileList])

  // Handle category change
  const handleCategoryChange = async (newCategoryId) => {
    if (!newCategoryId) return

    setIsLoading(true)
    try {
      setSatsueiMemoCategoryId(newCategoryId)
      setSatsueiGuideId('')

      // Clear existing form data and column data when category changes
      setFormData({})
      setSatsueiMemoColumnDataList([])

      // Load new column data for the selected category
      // If we have an existing objectId, use it; otherwise use a dummy ID to get column structure
      // Note: The getObject API returns column data for all categories, we'll filter by categoryId
      const targetObjectId = objectId || '1'
      const object = await satsueiDataTorokuService.getObject(targetObjectId, newCategoryId, true)

      // Check for business logic errors in the response
      const hasError = handleApiResponse(object, 'カテゴリーの列データ取得に失敗しました')
      if (hasError) {
        return
      }

      // Update column data list with the new category's columns
      if (object.satsueiMemoColumnDataList) {
        // Filter columns to only show those for the selected category
        // Include both category-specific columns and global columns (categoryId = 0)
        const filteredColumns = object.satsueiMemoColumnDataList.filter(column =>
          column.satsueiMemoCategoryId === parseInt(newCategoryId) || column.satsueiMemoCategoryId === 0
        )
        setSatsueiMemoColumnDataList(filteredColumns)

        // Initialize form data from the new column data
        const initialFormData = {}
        filteredColumns.forEach(column => {
          if (column.deleteFlg === 0) {
            // For choice fields, check the option values
            if (column.satsueiMemoColumnValueList && column.satsueiMemoColumnValueList.length > 0) {
              // First, check if user has made any selections (value === "1")
              let selectedOptions = column.satsueiMemoColumnValueList
                .filter(option => option.deleteFlg === 0 && option.value === "1")
                .map(option => option.key)

              // If no user selections, fall back to initial values (initialValueFlg === 1)
              if (selectedOptions.length === 0) {
                selectedOptions = column.satsueiMemoColumnValueList
                  .filter(option => option.deleteFlg === 0 && option.initialValueFlg === 1)
                  .map(option => option.key)
              }

              if (selectedOptions.length > 0) {
                // For multi-select fields (checkboxes, multi-select), join with commas
                if (column.columnType === 6 || column.columnType === 7) {
                  initialFormData[column.key] = selectedOptions.join(',')
                } else {
                  // For single-select fields (radio buttons, dropdown), use first selected
                  initialFormData[column.key] = selectedOptions[0]
                }
              } else {
                // Store backend format values in formData
                let value = column.value || column.initialValue || ''
                initialFormData[column.key] = value
              }
            } else {
              // Store backend format values in formData
              let value = column.value || column.initialValue || ''
              initialFormData[column.key] = value
            }
          }
        })
        setFormData(initialFormData)
      }

      await Promise.all([
        loadSatsueiGuideList(newCategoryId),
        loadSatsueiMemoForm(objectId, newCategoryId, true)
      ])
    } catch (err) {
      handleApiError(err, 'カテゴリーの変更に失敗しました')
      console.error('Failed to change category:', err)
    } finally {
      setIsLoading(false)
    }
  }

  // Handle image folder change
  const handleImageFolderChange = async (folderId) => {
    setSelectedImageFolderId(folderId)
    if (objectId && folderId !== 99) {
      await loadImageList(objectId, folderId)
    } else {
      await loadImageList(objectId)
    }
  }

  // Register or update object
  const registerObject = async () => {
    if (!objectId) {
      handleValidationErrors('IDを入力してください')
      return
    }

    setIsLoading(true)
    setError(null)
    let objectExists = false

    try {
      // Check if object exists to determine whether to create or update
      objectExists = await checkObjectExists(objectId)

      // Prepare the data structure to match backend expectations
      const data = {
        objectId,
        satsueiMemoCategoryId,
        kyotenId,
        satsueiGuideId,
        registSatsueiMemoColumnList: satsueiMemoColumnDataList.map(column => ({
          satsueiMemoCategoryId: column.satsueiMemoCategoryId,
          satsueiMemoColumnId: column.satsueiMemoColumnId,
          satsueiMemoColumnName: column.satsueiMemoColumnName,
          columnType: column.columnType,
          deleteFlg: column.deleteFlg,
          key: column.key,
          requiredFlg: column.requiredFlg,
          initialValue: column.initialValue,
          value: formData[column.key] !== undefined ? formData[column.key] : (column.value || column.initialValue || ''),
          satsueiMemoColumnValueList: column.satsueiMemoColumnValueList ? column.satsueiMemoColumnValueList.map(option => {
            // Get the current form value for this column
            const currentValue = formData[column.key] !== undefined ? formData[column.key] : (column.value || column.initialValue || '')

            // For multi-select fields (checkboxes, multi-select), check if option.key is in the comma-separated values
            let isSelected = false
            if (column.columnType === 6 || column.columnType === 7) {
              // For checkboxes and multi-select, split the comma-separated values and check if option.key is included
              const selectedValues = currentValue.split(',').map(v => v.trim()).filter(v => v)
              isSelected = selectedValues.includes(option.key)
            } else {
              // For single-select fields (radio buttons, dropdown), direct comparison
              isSelected = currentValue === option.key
            }

            return {
              satsueiMemoCategoryId: option.satsueiMemoCategoryId,
              satsueiMemoColumnId: option.satsueiMemoColumnId,
              satsueiMemoColumnValueId: option.satsueiMemoColumnValueId,
              satsueiMemoColumnValueName: option.satsueiMemoColumnValueName,
              deleteFlg: option.deleteFlg,
              initialValueFlg: option.initialValueFlg,
              key: option.key,
              value: isSelected ? "true" : "false"
            }
          }) : []
        }))
      }

      // Call appropriate API based on whether object exists
      const result = objectExists
        ? await satsueiDataTorokuService.updateObject(data)
        : await satsueiDataTorokuService.addObject(data)

      // Check for business logic errors in the response
      const hasError = handleApiResponse(result, objectExists ? '更新に失敗しました' : '登録に失敗しました')
      if (hasError) {
        return
      }

      // Update image order if images have been reordered
      if (imageList && imageList.length > 0) {
        try {
          const orderResult = await satsueiDataTorokuService.updateImageOrderIndex(objectId, imageList)
          // Check for business logic errors in the image order update response
          const hasOrderError = handleApiResponse(orderResult, '画像の順序更新に失敗しました')
          if (hasOrderError) {
            return // Fail the entire registration if image order update fails
          }
        } catch (orderError) {
          handleApiError(orderError, '画像の順序更新に失敗しました')
          return // Fail the entire registration if image order update fails
        }
      }

      // Update movie order if movies have been reordered
      if (movieList && movieList.length > 0) {
        try {
          const orderResult = await satsueiDataTorokuService.updateMovieOrderIndex(objectId, movieList)
          // Check for business logic errors in the movie order update response
          const hasOrderError = handleApiResponse(orderResult, '動画の順序更新に失敗しました')
          if (hasOrderError) {
            return // Fail the entire registration if movie order update fails
          }
        } catch (orderError) {
          handleApiError(orderError, '動画の順序更新に失敗しました')
          return // Fail the entire registration if movie order update fails
        }
      }

      // Update other file order if other files have been reordered
      if (otherFileList && otherFileList.length > 0) {
        try {
          const orderResult = await satsueiDataTorokuService.updateOtherFileOrderIndex(objectId, otherFileList)
          // Check for business logic errors in the other file order update response
          const hasOrderError = handleApiResponse(orderResult, 'その他ファイルの順序更新に失敗しました')
          if (hasOrderError) {
            return // Fail the entire registration if other file order update fails
          }
        } catch (orderError) {
          handleApiError(orderError, 'その他ファイルの順序更新に失敗しました')
          return // Fail the entire registration if other file order update fails
        }
      }

      // Reload data after registration/update
      await loadObject(objectId)

      return result
    } catch (err) {
      handleApiError(err, objectExists ? '更新に失敗しました' : '登録に失敗しました')
      console.error('Failed to register/update object:', err)
      throw err
    } finally {
      setIsLoading(false)
    }
  }

  // Delete object
  const deleteObject = async () => {
    if (!objectId) return

    setIsLoading(true)
    setError(null)

    try {
      const result = await satsueiDataTorokuService.deleteObject(objectId)

      // Check for business logic errors in the response
      const hasError = handleApiResponse(result, '削除に失敗しました')
      if (hasError) {
        return
      }

      // Clear data after deletion
      setObjectData(null)
      setObjectId('')
      setSatsueiMemoCategoryId('')
      setKyotenId('')
      setSatsueiGuideId('')
      setCreateUserName('')
      setCreateDatetime('')
      setUpdateUserName('')
      setUpdateDatetime('')
      setImageList([])
      setMovieList([])
      setOtherFileList([])
      setFormData({})

      return result
    } catch (err) {
      handleApiError(err, '削除に失敗しました')
      console.error('Failed to delete object:', err)
      throw err
    } finally {
      setIsLoading(false)
    }
  }

  // Copy object
  const copyObject = async (destinationId) => {
    if (!objectId || !destinationId) {
      handleValidationErrors('コピー元とコピー先のIDを入力してください')
      return
    }

    setIsLoading(true)
    setError(null)

    try {
      const data = {
        copySourceObjectId: objectId,
        copyDestinationObjectId: destinationId,
        satsueiMemoCategoryId
      }

      const result = await satsueiDataTorokuService.copyObjectData(data)

      // Check for business logic errors in the response
      const hasError = handleApiResponse(result, 'コピーに失敗しました')
      if (hasError) {
        return
      }

      return result
    } catch (err) {
      handleApiError(err, 'コピーに失敗しました')
      console.error('Failed to copy object:', err)
      throw err
    } finally {
      setIsLoading(false)
    }
  }

  // Get next/previous object
  const getAdjacentObject = async (direction) => {
    if (!objectId) return

    setIsLoading(true)
    setError(null)

    try {
      const kbn = direction === 'next' ? '2' : '1'
      const result = await satsueiDataTorokuService.getObjectId4PrevNext(objectId, kbn)

      // Check for business logic errors in the response
      const hasError = handleApiResponse(result, '隣接オブジェクトの取得に失敗しました')
      if (hasError) {
        return
      }

      if (result.target) {
        await loadObject(result.target)
      }

    } catch (err) {
      handleApiError(err, '隣接オブジェクトの取得に失敗しました')
      console.error('Failed to get adjacent object:', err)
    } finally {
      setIsLoading(false)
    }
  }

  // Get max object ID
  const getMaxObjectId = async () => {
    setIsLoading(true)
    setError(null)

    try {
      const result = await satsueiDataTorokuService.getMaxObjectId()
      return result.objectId
    } catch (err) {
      handleApiError(err, '最大IDの取得に失敗しました')
      console.error('Failed to get max object ID:', err)
      throw err
    } finally {
      setIsLoading(false)
    }
  }

  // Check if object exists
  const checkObjectExists = async (id) => {
    try {
      const result = await satsueiDataTorokuService.getObjectCount(id)
      return result.objectCount > 0
    } catch (err) {
      console.error('Failed to check object existence:', err)
      return false
    }
  }
  
  // Update form data
  const updateFormData = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }))
  }

  // Toggle selection mode
  const toggleSelectionMode = () => {
    setIsSelectionMode(!isSelectionMode)
    if (isSelectionMode) {
      setSelectedItems([])
    }
  }

  // Toggle item selection
  const toggleItemSelection = (itemId) => {
    setSelectedItems(prev =>
      prev.includes(itemId)
        ? prev.filter(id => id !== itemId)
        : [...prev, itemId]
    )
  }

  // Select all items
  const selectAllItems = () => {
    const currentList = selectedMediaType === 'image' ? imageList :
      selectedMediaType === 'movie' ? movieList : otherFileList

    setSelectedItems(currentList.map(item =>
      selectedMediaType === 'image' ? item.objectImageId :
        selectedMediaType === 'movie' ? item.objectMovieId : item.objectOtherFileId
    ))
  }

  // Clear all selections
  const clearAllSelections = () => {
    setSelectedItems([])
  }

  return {
    // State
    objectId,
    isLoading,
    error,
    objectData,
    satsueiMemoCategoryId,
    kyotenId,
    satsueiGuideId,
    createUserName,
    createDatetime,
    updateUserName,
    updateDatetime,
    satsueiMemoCategories,
    kyotenList,
    satsueiGuideList,
    imageFolderList,
    selectedImageFolderId,
    satsueiMemoForm,
    formScript,
    formData,
    satsueiMemoColumnDataList,
    imageList,
    movieList,
    otherFileList,
    selectedMediaType,
    isSelectionMode,
    selectedItems,
    objectIdAutoFlg,
    objectExists,

    // Actions
    setObjectId,
    setError,
    setSatsueiMemoCategoryId,
    setKyotenId,
    setSatsueiGuideId,
    setSelectedImageFolderId,
    setSelectedMediaType,
    setFormData,
    setImageList,
    setOtherFileList,
    setIsSelectionMode,
    setSelectedItems,
    loadObject,
    handleCategoryChange,
    handleImageFolderChange,
    registerObject,
    deleteObject,
    copyObject,
    getAdjacentObject,
    getMaxObjectId,
    checkObjectExists,
    updateFormData,
    toggleSelectionMode,
    toggleItemSelection,
    selectAllItems,
    clearAllSelections,
    loadImageList,
    loadMovieList,
    loadOtherFileList,
    loadImageFolderList
  }
}
